// Workflow execution utilities — condition evaluation, state persistence, action execution.
import { parseRepo, createIssue, pushFile, createPR, readFile } from './githubService';

// ── Template string resolution ───────────────────────────────────────────────
// Replaces {field_name} placeholders with values from a context map.
// Context is built from connected collections/trigger data at execution time.

export function resolveTemplate(template, context = {}) {
  if (!template || typeof template !== 'string') return template || '';
  return template.replace(/\{([^}]+)\}/g, (match, key) => {
    const trimmed = key.trim();
    if (trimmed in context) return String(context[trimmed] ?? '');
    const lower = trimmed.toLowerCase();
    for (const [k, v] of Object.entries(context)) {
      if (k.toLowerCase() === lower) return String(v ?? '');
    }
    return match;
  });
}

export async function buildTemplateContext(actionNode, nodes, triggerRecordId, dataService, workspaceId) {
  const ctx = {};
  ctx.timestamp = new Date().toISOString();
  ctx.date = new Date().toLocaleDateString('en-GB');
  ctx.node_label = actionNode.data?.label || '';

  const colNodes = nodes.filter(n => n.type === 'collectionNode');
  for (const col of colNodes) {
    const prefix = (col.data?.label || col.id).replace(/\s+/g, '_').toLowerCase();
    let records = [];
    if (dataService && workspaceId) {
      try { records = await dataService.getRecords(workspaceId, col.id); } catch {}
    }
    const lastRecord = records[records.length - 1];
    if (lastRecord) {
      for (const field of (col.data?.fields || [])) {
        const fieldKey = (field.name || field.id).replace(/\s+/g, '_').toLowerCase();
        ctx[`${prefix}.${fieldKey}`] = lastRecord.values?.[field.id] ?? '';
        ctx[fieldKey] = lastRecord.values?.[field.id] ?? '';
      }
    }
    ctx[`${prefix}.count`] = records.length;

    if (triggerRecordId) {
      const triggerRecord = records.find(r => r.id === triggerRecordId);
      if (triggerRecord) {
        for (const field of (col.data?.fields || [])) {
          const fieldKey = (field.name || field.id).replace(/\s+/g, '_').toLowerCase();
          ctx[`record.${fieldKey}`] = triggerRecord.values?.[field.id] ?? '';
          ctx[fieldKey] = triggerRecord.values?.[field.id] ?? '';
        }
      }
    }
  }

  const fnNodes = nodes.filter(n => n.type === 'functionNode');
  for (const fn of fnNodes) {
    const prefix = (fn.data?.label || fn.id).replace(/\s+/g, '_').toLowerCase();
    const tasks = fn.data?.tasks || [];
    ctx[`${prefix}.task_count`] = tasks.length;
    const pending = tasks.filter(t => {
      const sf = (fn.data?.taskFields || []).find(f => f.name?.toLowerCase() === 'status');
      return sf ? t.values?.[sf.id] !== 'Done' : true;
    });
    ctx[`${prefix}.pending_count`] = pending.length;
  }

  return ctx;
}

// ── Condition evaluation ──────────────────────────────────────────────────────

export async function evaluateCondition(conditions = [], logic = 'and', nodes = [], recordId = null, dataService = null, workspaceId = null) {
  if (!conditions.length) return true;
  const results = await Promise.all(conditions.map(async (cond) => {
    const colNode = nodes.find(n => n.id === cond.collectionId);
    if (!colNode) return false;
    let records = [];
    if (dataService && workspaceId) {
      try { records = await dataService.getRecords(workspaceId, colNode.id); } catch {}
    }
    if (recordId) {
      const rec = records.find(r => r.id === recordId);
      if (!rec) return false;
      return compareValues(rec.values?.[cond.fieldId], cond.operator, cond.value);
    }
    return records.some(r => compareValues(r.values?.[cond.fieldId], cond.operator, cond.value));
  }));
  return logic === 'or' ? results.some(Boolean) : results.every(Boolean);
}

function compareValues(actual, operator, expected) {
  const a  = String(actual  ?? '').toLowerCase().trim();
  const e  = String(expected ?? '').toLowerCase().trim();
  const na = Number(actual);
  const ne = Number(expected);
  switch (operator) {
    case 'eq':       return a === e;
    case 'neq':      return a !== e;
    case 'gt':       return !isNaN(na) && !isNaN(ne) && na > ne;
    case 'gte':      return !isNaN(na) && !isNaN(ne) && na >= ne;
    case 'lt':       return !isNaN(na) && !isNaN(ne) && na < ne;
    case 'lte':      return !isNaN(na) && !isNaN(ne) && na <= ne;
    case 'contains': return a.includes(e);
    default:         return false;
  }
}

// ── Workflow state persistence (localStorage) ─────────────────────────────────

const KEY = id => `projectly_wf_${id}`;

function dispatch(state) {
  try {
    window.dispatchEvent(new CustomEvent('projectly_workflow_update', { detail: state }));
  } catch {}
}

export function saveWorkflowState(state) {
  try {
    const next = { ...state, updatedAt: new Date().toISOString() };
    localStorage.setItem(KEY(state.id), JSON.stringify(next));
    dispatch(next);
  } catch {}
}

export function loadWorkflowState(workflowId) {
  try { return JSON.parse(localStorage.getItem(KEY(workflowId)) || 'null'); } catch { return null; }
}

export function clearWorkflowState(workflowId) {
  localStorage.removeItem(KEY(workflowId));
  dispatch({ id: workflowId, status: 'deleted' });
}

export function getAllWorkflows() {
  const results = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith('projectly_wf_')) {
      try { results.push(JSON.parse(localStorage.getItem(k))); } catch {}
    }
  }
  return results.sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt));
}

// ── Step helpers ──────────────────────────────────────────────────────────────

export function addWorkflowStep(workflowId, step) {
  const state = loadWorkflowState(workflowId);
  if (!state) return;
  const steps = [...(state.steps || []), { ...step, startedAt: new Date().toISOString() }];
  saveWorkflowState({ ...state, steps, currentNodeId: step.nodeId, currentNodeLabel: step.nodeLabel });
}

export function completeWorkflowStep(workflowId, nodeId, detail = '') {
  const state = loadWorkflowState(workflowId);
  if (!state) return;
  const steps = (state.steps || []).map(s =>
    s.nodeId === nodeId && s.status === 'running'
      ? { ...s, status: 'completed', completedAt: new Date().toISOString(), detail }
      : s
  );
  saveWorkflowState({ ...state, steps });
}

export function failWorkflowStep(workflowId, nodeId, detail = '') {
  const state = loadWorkflowState(workflowId);
  if (!state) return;
  const steps = (state.steps || []).map(s =>
    s.nodeId === nodeId && s.status === 'running'
      ? { ...s, status: 'failed', completedAt: new Date().toISOString(), detail }
      : s
  );
  saveWorkflowState({ ...state, steps, status: 'failed' });
}

// ── Action execution (called by PipelineCanvas) ───────────────────────────────

function resolveTargetCollectionIds(actionNode, nodes = [], edges = []) {
  const ids = edges
    .filter(e => e.source === actionNode.id)
    .map(e => nodes.find(n => n.id === e.target))
    .filter(n => n?.type === 'collectionNode')
    .map(n => n.id);
  return [...new Set(ids)];
}

function resolveConnectionNode(actionNode, nodes = [], edges = [], fallbackId) {
  for (const e of edges.filter(e => e.source === actionNode.id)) {
    const tgt = nodes.find(n => n.id === e.target);
    if (tgt?.type === 'connectionNode') return tgt;
  }
  return fallbackId ? nodes.find(n => n.id === fallbackId) : null;
}

function resolveFieldForCollection(col, fieldId, fieldName, value) {
  const fields = col.data?.fields || [];
  const field = fields.find(f => f.id === fieldId) || fields.find(f => f.name === fieldName);
  if (!field) return { error: `no field "${fieldName || fieldId}"` };
  if (field.type === 'select' && Array.isArray(field.options) && field.options.length) {
    const ok = field.options.some(o => String(o).toLowerCase().trim() === String(value).toLowerCase().trim());
    if (!ok) return { error: `"${value}" is not a valid option for "${field.name}"` };
  }
  return { field };
}

export async function executeAction(actionNode, nodes, dataService, workspaceId, addInboxItem, triggerRecordId, edges = []) {
  const {
    actionType, fieldId, fieldName, fieldValue,
    filterFieldId, filterValue,
    notificationSubject, notificationText,
  } = actionNode.data || {};

  const tplCtx = await buildTemplateContext(actionNode, nodes, triggerRecordId, dataService, workspaceId);
  const r = (s) => resolveTemplate(s, tplCtx);

  const targetCollectionIds = resolveTargetCollectionIds(actionNode, nodes, edges);

  if (actionType === 'update_field' && fieldId && fieldValue !== undefined) {
    if (!targetCollectionIds.length) throw new Error('update_field action is not connected to a collection');
    const resolvedVal = r(fieldValue);
    const hasFilter = filterFieldId && filterValue !== undefined && filterValue !== '';
    let updated = 0;
    const failures = [];
    for (const targetId of targetCollectionIds) {
      const col = nodes.find(n => n.id === targetId);
      if (!col) { failures.push(`${targetId}: collection not found`); continue; }
      const { field, error } = resolveFieldForCollection(col, fieldId, fieldName, resolvedVal);
      if (error) { failures.push(`${col.data?.label || targetId}: ${error}`); continue; }
      const records = await dataService.getRecords(workspaceId, targetId);
      for (const rec of records) {
        if (hasFilter) {
          const actual = String(rec.values?.[filterFieldId] ?? '').toLowerCase().trim();
          const expected = String(r(filterValue)).toLowerCase().trim();
          if (actual !== expected) continue;
        }
        await dataService.patchRecord(workspaceId, targetId, rec.id, { [field.id]: resolvedVal });
      }
      updated++;
    }
    if (failures.length) console.warn('[executeAction:update_field] skipped targets:', failures.join('; '));
    if (updated === 0) throw new Error(`update_field failed for all targets: ${failures.join('; ') || 'no matching collections'}`);

  } else if (actionType === 'send_notification' && notificationText) {
    const channels = actionNode.data.messageChannels || { inbox: true, email: false, whatsapp: false };
    const recipientType = actionNode.data.recipientType || 'owner';
    let recipientAddress = '';
    if (recipientType === 'collection_field' && actionNode.data.recipientCollectionId && actionNode.data.recipientFieldId) {
      const rcol = nodes.find(n => n.id === actionNode.data.recipientCollectionId);
      if (rcol) {
        const records = await dataService.getRecords(workspaceId, rcol.id);
        const rec = triggerRecordId ? records.find(rx => rx.id === triggerRecordId) : records[records.length - 1];
        if (rec) recipientAddress = String(rec.values?.[actionNode.data.recipientFieldId] ?? '');
      }
    } else if (recipientType === 'specific') {
      recipientAddress = r(actionNode.data.recipientAddress || '');
    }

    addInboxItem?.({
      id: `inbox_action_${Date.now()}`,
      type: 'notification',
      subject: r(notificationSubject || actionNode.data.label || 'Workflow notification'),
      from: 'Workflow', fromNode: 'Pipeline',
      preview: r(notificationText).slice(0, 120),
      body: r(notificationText),
      recipientAddress: recipientAddress || undefined,
      channels,
      timestamp: new Date().toISOString(),
      read: false, starred: false, folder: 'inbox',
    });

  } else if (actionType === 'create_record') {
    if (!targetCollectionIds.length) throw new Error('create_record action is not connected to a collection');
    const rawValues = actionNode.data.recordValues || {};
    const resolvedValues = {};
    for (const [k, v] of Object.entries(rawValues)) {
      resolvedValues[k] = r(String(v));
    }
    await dataService.addRecord(workspaceId, targetCollectionIds[0], resolvedValues);

  } else if (actionType === 'create_function_task') {
    const fnId = actionNode.data.functionNodeId;
    if (!fnId) throw new Error('No function node selected for task creation.');
    try {
      const pipeline  = await dataService.getPipeline(workspaceId);
      const pipeNodes = [...(pipeline.nodes || [])];
      const fnIdx     = pipeNodes.findIndex(n => n.id === fnId);
      if (fnIdx !== -1) {
        const fn = pipeNodes[fnIdx];
        const taskFields = fn.data?.taskFields || [];
        const fieldMap = actionNode.data.taskFieldMap || {};
        const newTask = {
          id: `t${Date.now()}`,
          values: {},
          createdAt: new Date().toISOString(),
          sourceRef: { actionNodeId: actionNode.id },
        };
        for (const tf of taskFields) {
          if (fieldMap[tf.id]) {
            newTask.values[tf.id] = r(fieldMap[tf.id]);
          } else if (tf.default) {
            newTask.values[tf.id] = tf.default;
          }
        }
        const tasks = [...(fn.data?.tasks || []), newTask];
        pipeNodes[fnIdx] = { ...fn, data: { ...fn.data, tasks } };
        await dataService.savePipeline(workspaceId, pipeNodes, pipeline.edges || []);
      }
    } catch (e) { console.error('[executeAction:create_function_task]', e); }

  } else if (actionType === 'update_function_task') {
    const fnId = actionNode.data.functionNodeId;
    const targetFieldId = actionNode.data.taskUpdateFieldId;
    const targetValue = actionNode.data.taskUpdateValue;
    const filterTaskFieldId = actionNode.data.taskFilterFieldId;
    const filterTaskValue = actionNode.data.taskFilterValue;
    if (!fnId || !targetFieldId) throw new Error('Function task update not fully configured.');
    try {
      const pipeline  = await dataService.getPipeline(workspaceId);
      const pipeNodes = [...(pipeline.nodes || [])];
      const fnIdx     = pipeNodes.findIndex(n => n.id === fnId);
      if (fnIdx !== -1) {
        const fn = pipeNodes[fnIdx];
        const tasks = (fn.data?.tasks || []).map(t => {
          if (filterTaskFieldId && filterTaskValue) {
            const actual = String(t.values?.[filterTaskFieldId] ?? '').toLowerCase().trim();
            if (actual !== String(r(filterTaskValue)).toLowerCase().trim()) return t;
          }
          return { ...t, values: { ...t.values, [targetFieldId]: r(targetValue) } };
        });
        pipeNodes[fnIdx] = { ...fn, data: { ...fn.data, tasks } };
        await dataService.savePipeline(workspaceId, pipeNodes, pipeline.edges || []);
      }
    } catch (e) { console.error('[executeAction:update_function_task]', e); }

  } else if (actionType === 'http_request') {
    const connNode = resolveConnectionNode(actionNode, nodes, edges, actionNode.data.connectionNodeId);
    const baseUrl = connNode?.data?.serviceUrl || '';
    const endpoint = actionNode.data.httpEndpoint || '';
    const method = actionNode.data.httpMethod || 'GET';
    const bodyStr = actionNode.data.httpBody || '';
    const headersStr = actionNode.data.httpHeaders || '';
    const url = r(baseUrl ? `${baseUrl.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}` : endpoint);
    if (!url) throw new Error('HTTP request requires a URL (set via Connection or directly).');
    const headers = { 'Content-Type': 'application/json' };
    if (connNode?.data?.authToken) {
      const authType = connNode.data.authType || 'bearer';
      if (authType === 'bearer') headers['Authorization'] = `Bearer ${connNode.data.authToken}`;
      else if (authType === 'api_key') headers['X-API-Key'] = connNode.data.authToken;
      else if (authType === 'basic') headers['Authorization'] = `Basic ${btoa(connNode.data.authToken)}`;
      else if (authType === 'header') Object.assign(headers, { 'Authorization': connNode.data.authToken });
    }
    if (connNode?.data?.customHeaders) {
      try { Object.assign(headers, JSON.parse(r(connNode.data.customHeaders))); } catch {}
    }
    if (headersStr.trim()) {
      try {
        const custom = JSON.parse(r(headersStr));
        Object.assign(headers, custom);
      } catch {}
    }
    const proxyHeaders = { ...headers };
    const proxyFetchOpts = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-Proxy-Target': url,
        'X-Proxy-Headers': JSON.stringify(proxyHeaders),
      },
    };
    if (method !== 'GET' && method !== 'HEAD' && bodyStr.trim()) {
      proxyFetchOpts.body = r(bodyStr);
    }
    const resp = await fetch('/api/proxy', proxyFetchOpts);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);

  } else if (actionType === 'github_create_issue') {
    const conn = await dataService.getConnections(workspaceId);
    const gh   = conn.github || {};
    if (!gh.token) throw new Error('GitHub not connected — add a Personal Access Token in Connections.');
    const repoStr = actionNode.data.githubRepo || gh.repo || '';
    const { owner, repo } = parseRepo(repoStr);
    if (!owner || !repo) throw new Error('GitHub repo not configured — set a repo in the action or Connections.');
    await createIssue(
      gh.token, owner, repo,
      r(actionNode.data.issueTitle || 'New issue from workflow'),
      r(actionNode.data.issueBody  || ''),
      actionNode.data.issueLabels ? actionNode.data.issueLabels.split(',').map(l => l.trim()).filter(Boolean) : [],
    );

  } else if (actionType === 'github_create_pr') {
    const conn   = await dataService.getConnections(workspaceId);
    const gh     = conn.github || {};
    if (!gh.token) throw new Error('GitHub not connected — add a Personal Access Token in Connections.');
    const repoStr = actionNode.data.githubRepo || gh.repo || '';
    const { owner, repo } = parseRepo(repoStr);
    if (!owner || !repo) throw new Error('GitHub repo not configured — set a repo in the action or Connections.');
    const base = actionNode.data.prBase || gh.branch || 'main';
    const head = actionNode.data.prHead;
    if (!head) throw new Error('GitHub PR action requires a head branch.');
    await createPR(
      gh.token, owner, repo,
      r(actionNode.data.prTitle || 'Pull request from workflow'),
      r(actionNode.data.prBody  || ''),
      head, base,
    );

  } else if (actionType === 'github_push_file') {
    const conn = await dataService.getConnections(workspaceId);
    const gh   = conn.github || {};
    if (!gh.token) throw new Error('GitHub not connected — add a Personal Access Token in Connections.');
    const repoStr = actionNode.data.githubRepo || gh.repo || '';
    const { owner, repo } = parseRepo(repoStr);
    if (!owner || !repo) throw new Error('GitHub repo not configured.');
    const branch  = actionNode.data.fileBranch || gh.branch || 'main';
    const path    = actionNode.data.filePath;
    const content = r(actionNode.data.fileContent || '');
    const message = r(actionNode.data.commitMessage || `Update ${path} via workflow`);
    if (!path) throw new Error('GitHub push_file action requires a file path.');
    let sha = null;
    try { ({ sha } = await readFile(gh.token, owner, repo, path, branch)); } catch {}
    await pushFile(gh.token, owner, repo, path, content, message, branch, sha);
  }
}
